import React, { useState, useEffect } from "react";
import "./PostComposer.css";
import { db, storage, auth } from "../firebaseConfig";
import { addDoc, collection, doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL} from "firebase/storage";
import { toast } from "react-toastify";
import { getNewPostExpiresAt } from "../utils/postLifecycle";


const PostComposer = ({ onSubmit }) => {
  const [text, setText] = useState("");
  const [topic, setTopic] = useState("");
  const [privacy, setPrivacy] = useState("real"); // real | usernameDept | agencyOnly | anonymous
  const [images, setImages] = useState([]);
  const [linkUrl, setLinkUrl] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [lastAnonLocal, setLastAnonLocal] = useState(null); // local cache to reflect immediate cooldown
  const [isPosting, setIsPosting] = useState(false);

  const sanitize = (v) => {
    try {
      let s = String(v ?? "");
      s = s.replace(/[\r\n]+/g, " ");
      s = s.replace(/[\u2012-\u2015]/g, " - ");
      s = s.replace(/[^\x20-\x7E]+/g, "");
      s = s.replace(/\s{2,}/g, " ").trim();
      return s;
    } catch {
      return v;
    }
  };

  const nameInitialLast = (p) => {
    if (!p) return "Officer";
    if (p.firstName && p.lastName) return `${p.firstName[0]}. ${p.lastName}`;
    if (p.firstInitialLastName)
      return p.firstInitialLastName.includes(".")
        ? p.firstInitialLastName.replace(".", ". ")
        : p.firstInitialLastName;
    return p.displayId || "Officer";
  };

  // Detect screen size
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 900);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load current user's profile to render privacy labels
  useEffect(() => {
    const run = async () => {
      const user = auth.currentUser;
      if (!user) return;
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) setProfileData(snap.data());
    };
    run();
  }, []);



const handleSubmit = async (e) => {
  e.preventDefault();
  if (isPosting || !text.trim()) return;

  try {
    setIsPosting(true);
    const user = auth.currentUser;
    if (!user) {
      toast.error("Please log in to post.");
      return;
    }

   const profileRef = doc(db, "users", user.uid);
const profileSnapshot = await getDoc(profileRef);
const profile = profileSnapshot.data();

// Enforce anonymous post limit: 1 per week (server-side timestamp)
if (privacy === "anonymous") {
  const lastAnonPost = profile?.lastAnonymousPost?.toDate?.();
  const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
  if (lastAnonPost && Date.now() - lastAnonPost.getTime() < oneWeekMs) {
    toast.error("Anonymous posting is limited to one per week.");
    return;
  }
  await updateDoc(profileRef, { lastAnonymousPost: serverTimestamp() });
}

const postData = {
  userId: user.uid,
  rank: profile?.rank || "",
  userName: profile?.firstInitialLastName || "Unknown",
  agency: profile?.agency || "N/A",
  text: text.trim(),
  topic: topic.trim().toLowerCase() || null,
  linkUrl: linkUrl.trim() || null,
  privacyTier: privacy,
  createdAt: serverTimestamp(),
  updatedAt: serverTimestamp(),
  likes: 0,
  views: 0,
  commentCount: 0,
  isHidden: false,
  authorDeleted: false,
  containsModeratedContent: false,
  pinned: false,
  images: [],
  imagePaths: [],
  expiresAt: getNewPostExpiresAt(),
};


    //  Upload up to 3 images if provided
    if (images && images.length > 0) {
      const toUpload = images.slice(0, 3);
      const urls = [];
      const paths = [];
      for (const file of toUpload) {
        const imagePath = `posts/${user.uid}/${Date.now()}_${file.name}`;
        const imgRef = ref(storage, imagePath);
        await uploadBytes(imgRef, file);
        const imgUrl = await getDownloadURL(imgRef);
        urls.push(imgUrl);
        paths.push(imagePath);
      }
      postData.images = urls;
      postData.imagePaths = paths;
    }

    //  Add post to Firestore
    await addDoc(collection(db, "posts"), postData);

    // If posted anonymously, reflect cooldown immediately in UI
    if (privacy === "anonymous") {
      setLastAnonLocal(new Date());
    }

    //  Reset UI
    setText("");
    setTopic("");
    setPrivacy("real");
    setImages([]);
    setLinkUrl("");
    toast.success("Post shared successfully!");

    if (onSubmit) onSubmit(); 

  } catch (err) {
    console.error("Error posting:", err);
    toast.error("Failed to share post. Try again.");
  } finally {
    setIsPosting(false);
  }
};


  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImages((prev) => [...prev, ...files].slice(0, 3));
  };

  // Dynamic labels (mirror CommentSection) + Anonymous cooldown handling
  const userAgency = sanitize(profileData?.agency || "");
  const userRank = sanitize(profileData?.rank || "Officer");
  const userIL = sanitize(nameInitialLast(profileData));
  const optReal = `${userRank} ${userIL}${userAgency ? ` - ${userAgency}` : ""}`;
  const optUserDept = `${userIL}${userAgency ? ` - ${userAgency}` : ""}`;
  const optAgencyOnly = userAgency || "Agency";

  // Anonymous availability calculation
  const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
  const lastAnonDate = lastAnonLocal || profileData?.lastAnonymousPost?.toDate?.();
  const nextAnonDate = lastAnonDate ? new Date(lastAnonDate.getTime() + oneWeekMs) : null;
  const anonBlocked = !!(nextAnonDate && Date.now() < nextAnonDate.getTime());
  const formatShort = (d) => {
    try {
      return new Intl.DateTimeFormat(undefined, { year: "2-digit", month: "numeric", day: "numeric" }).format(d);
    } catch {
      return "soon";
    }
  };
  const anonLabel = anonBlocked ? `Anonymous (${formatShort(nextAnonDate)})` : "Anonymous";

  // If anonymous becomes blocked while selected, switch to real
  useEffect(() => {
    if (privacy === "anonymous" && anonBlocked) setPrivacy("real");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anonBlocked]);

  const composerBody = (
    <form className="composer-form" onSubmit={handleSubmit}>
      <textarea
        placeholder="Share your thoughts..."
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        required
      />

      <div className="composer-options">
      <input
  type="text"
  placeholder="Topic (optional, max 40 chars)"
  value={topic}
  maxLength={41}
  onChange={(e) => setTopic(e.target.value)}
/>

        <input
          type="url"
          placeholder="Video or article link (optional)"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
        />

        <label className="image-upload">
  Add Image(s) (max 3)
  <input
    type="file"
    accept="image/*"
    multiple
    onChange={handleImageChange}
  />
</label>

        <select
          className="privacy-select"
          value={privacy}
          onChange={(e) => setPrivacy(e.target.value)}
          title="Choose posting mode"
        >
          <option value="real">{optReal}</option>
          <option value="usernameDept">{optUserDept}</option>
          <option value="agencyOnly">{optAgencyOnly}</option>
          <option value="anonymous" disabled={anonBlocked}>{anonLabel}</option>
        </select>
        {anonBlocked && (
          <div className="anon-limit-hint" style={{ fontSize: 12, color: "#6b7280" }}>
            Anonymous available on {formatShort(nextAnonDate)}.
          </div>
        )}
      </div>

 {images && images.length > 0 && (
          <>
            
            <div className="composer-previews">
              {images.map((f, idx) => {
                const url = URL.createObjectURL(f);
                return (
                  <div key={idx} className="thumb">
                    <div className="thumb-image">
                      <img src={url} alt={`Selected ${idx + 1}`} onLoad={() => URL.revokeObjectURL(url)} />
                    </div>
                    <button
                      type="button"
                      className="remove"
                      onClick={() => setImages((prev) => prev.filter((_, i) => i !== idx))}
                      aria-label="Remove image"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}

      <button
        type="submit"
        className="post-btn"
        disabled={isPosting || !text.trim()}
        title={!text.trim() ? "Post text required" : "Post"}
      >
        {isPosting ? "Posting..." : "Post"}
      </button>
    </form>
  );
return <div className="composer-inline">{composerBody}</div>;
};

export default PostComposer;
